import React, { FunctionComponent } from 'react'
import { IconSizes, PaymentIcon, Span, Text, useThemeClass, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import styles from './style.module.css'
import clsx from 'clsx'
import { useCheckoutSizes } from '../../utils/responsive'
import { useApplePayFeature } from '../../state'
import { useCheckoutColor } from '../../utils/color'
import { useTranslation } from '../../utils/convergence'

interface Props {
  iconSize?: IconSizes
  reversed?: boolean
  showLearnMore?: boolean
  handleToggleTooltip?: () => void
  tooltipRef?: React.RefObject<HTMLElement>
  testNameSpace?: string
  validatePaymentType?: boolean
  ariaDescribedBy?: string
}

export const PaymentMethodApplePay: FunctionComponent<Props> = ({
  iconSize,
  reversed = false,
  showLearnMore = false,
  handleToggleTooltip = () => {},
  testNameSpace = '',
  validatePaymentType = false,
  ariaDescribedBy = '',
}) => {
  const { t } = useTranslation()
  const { textSize, smallTextSize } = useCheckoutSizes()
  const containerClasses = clsx(styles.container, reversed && styles.reversed)
  const applePay = useApplePayFeature()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { textColor } = useCheckoutColor({ hasError: !applePay.enabledForConsumerLending && validatePaymentType })
  const themeClass = useThemeClass()
  const textClass = clsx(styles.text, styles[themeClass])

  const tooltipButtonClasses = clsx(styles.triggerButton, styles[themeClass])

  const learnMore = (): JSX.Element => {
    return (
      <button type='button' className={tooltipButtonClasses} onClick={handleToggleTooltip} aria-describedby={ariaDescribedBy}>
        <Text size={textSize} bold renderAs='span' underline>
          {t('paymentMethod:learnMore')}
        </Text>
      </button>
    )
  }

  return (
    <span className={containerClasses} {...(testNameSpace.length > 0 && { 'data-testid': testNameSpace })}>
      <span className={styles.icon}>
        <PaymentIcon.Applepay size={iconSize} title={t('paymentMethod:applePay')} />
      </span>
      <span className={styles.applePayContainer}>
        <span className={textClass}>
          <Span bold={isArcadeDesignLanguage} size={textSize} color={textColor}>
            {t('paymentMethod:applePay')}
          </Span>
        </span>
        {!applePay.enabledForConsumerLending && validatePaymentType && (
          <Span size={smallTextSize} color='Fire'>
            {t(`paymentMethod:errors.applePayNotAllowed`)}
          </Span>
        )}
      </span>
      {showLearnMore && learnMore()}
    </span>
  )
}
